import { useState, type FormEvent, type JSX } from 'react';
import { useNavigate } from '@tanstack/react-router';

import { shaktiApi, ApiError } from '../api/client';
import { useAuthStore } from '../stores/auth';

/**
 * H3.1 (round-4) — PM/PO sign-in page.
 *
 * Round-3 shipped an `AuthBoundary` that showed "Signed out" forever
 * because there was no UI to actually sign in. This page provides two
 * paths:
 *
 *   1. **Paste API key**: the minimum-viable path. An operator runs
 *      `shakti auth init --email you@example.com` in a terminal,
 *      copies the printed `sk_live_…` key, pastes it here. The form
 *      validates the key via `GET /v1/auth/me`, stores token + org_id
 *      in the auth store, and redirects to the dashboard.
 *
 *   2. **Continue with WorkOS** (opt-in): hits `POST /v1/auth/login`
 *      and follows the returned redirect_url. Disabled when the
 *      server does not have auth provider credentials configured.
 *
 * Success path: token + organizationId land in `useAuthStore`,
 * navigate to `/dashboard`, `ProjectSwitcher` fetches projects, etc.
 */
export function LoginRoute(): JSX.Element {
  const navigate = useNavigate();
  const login = useAuthStore((s) => s.login);

  const [apiKey, setApiKey] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    const trimmed = apiKey.trim();
    if (!trimmed.startsWith('sk_live_') && !trimmed.startsWith('sk_test_')) {
      setError('API key must start with sk_live_ or sk_test_');
      return;
    }

    setBusy(true);
    try {
      // Temporarily drop the key into the auth store so `shaktiApi`
      // picks it up on the next request.
      login(trimmed, {
        id: 'unknown',
        email: 'unknown',
        displayName: 'Signed-in user',
        roles: [],
      });

      const me = await shaktiApi.getAuthMe();
      if (!me.authenticated || !me.org_id) {
        throw new ApiError(401, null, 'key did not resolve to a tenant');
      }

      // Re-populate with the real profile.
      login(trimmed, {
        id: me.user_id ?? 'unknown',
        email: 'unknown',
        displayName: 'Signed-in user',
        roles: [me.role ?? 'member'],
        organizationId: me.org_id,
      });

      await navigate({ to: '/dashboard' });
    } catch (err) {
      // Clear the token we speculatively stored.
      useAuthStore.getState().logout();
      setError(
        err instanceof ApiError
          ? `Sign-in rejected (${err.status}): ${err.body?.error ?? err.message}`
          : err instanceof Error
            ? err.message
            : 'Sign-in failed',
      );
    } finally {
      setBusy(false);
    }
  }

  async function startWorkOsFlow(): Promise<void> {
    setError(null);
    setBusy(true);
    try {
      const resp = await fetch('/v1/auth/login', { method: 'POST' });
      if (!resp.ok) {
        throw new Error(`login start failed: ${resp.status}`);
      }
      const body: { redirect_url?: string } = await resp.json();
      if (!body.redirect_url) {
        throw new Error('auth provider did not return a redirect URL');
      }
      window.location.href = body.redirect_url;
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Auth provider unreachable',
      );
      setBusy(false);
    }
  }

  return (
    <main
      className="grid min-h-screen place-items-center bg-bg-base text-fg-primary"
      aria-labelledby="signin-title"
    >
      <section
        className="shakti-card flex w-full max-w-md flex-col gap-6 p-8"
        data-testid="login-card"
      >
        <header className="flex flex-col gap-2">
          <span className="shakti-mono text-2xs uppercase tracking-wider text-fg-muted">
            Shakti
          </span>
          <h1 id="signin-title" className="text-2xl font-semibold">
            Sign in to continue
          </h1>
          <p className="text-sm text-fg-secondary">
            First-time setup? Run{' '}
            <code className="shakti-mono text-xs">shakti auth init --email you@example.com</code>{' '}
            in a terminal to mint your first admin key.
          </p>
        </header>

        <form onSubmit={submit} className="flex flex-col gap-3">
          <label htmlFor="api-key" className="text-xs uppercase tracking-wider text-fg-muted">
            API key
          </label>
          <input
            id="api-key"
            type="password"
            autoComplete="off"
            placeholder="sk_live_…"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            className="shakti-mono rounded-sm border border-border-subtle bg-bg-surface px-3 py-2 text-sm"
            disabled={busy}
            required
            data-testid="login-apikey-input"
          />
          <button
            type="submit"
            disabled={busy || apiKey.trim().length < 8}
            className="mt-2 rounded-sm bg-fg-primary px-3 py-2 text-sm font-semibold text-bg-base disabled:opacity-50"
            data-testid="login-submit"
          >
            {busy ? 'Signing in…' : 'Sign in with API key'}
          </button>
        </form>

        <div className="flex items-center gap-2 text-2xs text-fg-muted">
          <div className="h-px flex-1 bg-border-subtle" />
          <span>or</span>
          <div className="h-px flex-1 bg-border-subtle" />
        </div>

        <button
          type="button"
          onClick={startWorkOsFlow}
          disabled={busy}
          className="rounded-sm border border-border-subtle bg-bg-surface px-3 py-2 text-sm text-fg-primary disabled:opacity-50"
          data-testid="login-workos"
        >
          Continue with configured identity provider
        </button>

        {error ? (
          <div
            role="alert"
            className="rounded-sm border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-200"
            data-testid="login-error"
          >
            {error}
          </div>
        ) : null}
      </section>
    </main>
  );
}
